import SimpleCard from '@/components/simple-card';
import { CompassIcon, Loader2, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchGuards } from "../../../redux/slices/guardSlice";
import { AppDispatch, RootState } from 'redux/store';
import { fetchTenants } from '../../../redux/slices/tenantSlice';
import { SetTable } from '@/components/SetTable';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { routeNames } from 'constants/constants';
import { fetchComplaints } from '../../../redux/slices/complaintsSlice';
import { toggleBuildingStatus } from '../../../redux/slices/buildingsSlice';

type Flat = {
  flatNumber: string;
};

type Parking = { name: string } | string;

export default function BuildingDetail() {
    const { buildingId } = useParams();
    const location = useLocation();
    const { building } = location.state || {};
    
    const [isActive, setIsActive] = useState(building.isActive);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch<AppDispatch>();

    const guards = useSelector((state: RootState) => state.guards?.data?.data);
    const { tenants } = useSelector((state: RootState) => state.tenants);
    const { complaints } = useSelector((state: RootState) => state.complaints);

    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchGuards({ assignedBuilding: buildingId }));
        dispatch(fetchComplaints(building._id));
        dispatch(fetchTenants({ building: buildingId, role: "Tenant", status: "Approved" }));
    }, [dispatch, buildingId]);

    const handleToggleActive = async () => {
        setLoading(true);
        await dispatch(toggleBuildingStatus({
            buildingId, isActive: !isActive, callBack: (data: any) => {
                if (data.success) {
                    toast.success(data.message);
                    setIsActive(!isActive);
                } else {
                    toast.error(data.data.error);
                }
                setLoading(false);
            }
        }));
    };

    // Table Headers for other sections
    const guardstableKeys = [{ name: "name" }, { username: "username" }, { contact: "contactNumber" }];
    const tenantstableKeys = [{ name: "name" }, { username: "username" }, { contact: "contactNumber" }, { flatNo: "flatNo" }];
    const complaintstableKeys = [{ title: "title" }, { ID: "complaintId" }, { Name: "filedBy.name" }, { flatNo: "filedBy.flatNo" }];

    // Data transformation for Flats and Parking
    const flatsList: Flat[] = building?.flatNumbers?.map((flat: string) => ({ flatNumber: flat })) || [];
    const parkingList: Parking[] = building?.parking || [];

    return (
        <>
            <h1 className="py-5 text-3xl font-bold tracking-tight">Building Details</h1>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <SimpleCard title="Total Tenants" number={tenants?.length || 0} description="+2% from last month" Icon={User} />
                <SimpleCard title="Total Guards" number={guards?.length || 0} description="+2% from last month" Icon={User} />
                <SimpleCard title="Total Complaints" number={complaints?.length || 0} description="+2% from last month" Icon={CompassIcon} />
            </div>

            <div className="container">
                <Card className="border border-gray-200 shadow-sm p-3">
                    <div className="flex justify-between">
                        <h2 className="text-2xl font-semibold mb-4">Details</h2>
                        <Button onClick={handleToggleActive} variant={isActive ? "destructive" : "default"} disabled={loading}>
                            {loading && <Loader2 className="animate-spin" />}
                            {isActive ? "Deactivate Building" : "Activate Building"}
                        </Button>
                    </div>
                    <div className="flex flex-col gap-4">
                        <p><strong>NAME: </strong> {building?.name || "N/A"}</p>
                        <p>
                            <strong>ADDRESS: </strong> {building?.location?.city}, {building?.location?.state}, {building?.location?.country}
                        </p>
                        <p><strong>STATUS: </strong> {isActive ? "Active" : "Inactive"}</p>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Guards Table */}
                <Card className="p-5 col-span-5">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-semibold mb-4">Guards</h2>
                        <Button variant="outline" onClick={() => navigate(routeNames.guards, { state: { buildingId } })}>
                            View all
                        </Button>
                    </div>
                    <SetTable data={guards?.slice(0, 5)} tableKeys={guardstableKeys} />
                </Card>

                {/* Tenants Table */}
                <Card className="p-5 col-span-7">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-semibold mb-4">Tenants</h2>
                        <Button variant="outline" onClick={() => navigate(routeNames.tenantsManagement, { state: { buildingId } })}>
                            View all
                        </Button>
                    </div>
                    <SetTable data={tenants?.slice(0, 5)} tableKeys={tenantstableKeys} />
                </Card>
            </div>

            {/* Complaints Table */}
            <Card className="p-5 mt-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-semibold mb-4">Complaints</h2>
                    <Button variant="outline" onClick={() => navigate(routeNames.management, { state: { buildingId } })}>
                        View all
                    </Button>
                </div>
                <SetTable data={complaints?.slice(0, 5)} tableKeys={complaintstableKeys} />
            </Card>

            {/* Compact Flats and Parking Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* Flats List */}
                <Card className="p-5">
                    <h2 className="text-2xl font-semibold mb-2">Flats List</h2>
                    <p className="mb-4 text-sm text-gray-600">Total Flats: {flatsList.length}</p>
                    <div className="flex flex-wrap gap-2">
                        {flatsList.length ? (
                            flatsList.map((flat: Flat) => (
                                <span 
                                    key={flat.flatNumber} 
                                    className="px-3 py-1 bg-gray-100 border rounded text-sm"
                                >
                                    {flat.flatNumber}
                                </span>
                            ))
                        ) : (
                            <span>No flats available</span>
                        )}
                    </div>
                </Card>

                {/* Parking Slots */}
                <Card className="p-5">
                    <h2 className="text-2xl font-semibold mb-2">Parking Slots</h2>
                    <p className="mb-4 text-sm text-gray-600">Total Parking Slots: {parkingList.length}</p>
                    <div className="flex flex-wrap gap-2">
                        {parkingList.length ? (
                            parkingList.map((parking: Parking, index: number) => (
                                <span 
                                    key={index} 
                                    className="px-3 py-1 bg-gray-100 border rounded text-sm"
                                >
                                    {typeof parking === 'object' ? parking.name : parking}
                                </span>
                            ))
                        ) : (
                            <span>No parking slots available</span>
                        )}
                    </div>
                </Card>
            </div>
        </>
    );
}
